import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  Map,
  FullscreenControl,
  NavigationControl,
  Source,
  Layer,
} from "@vis.gl/react-maplibre";

import { Flex, Button, Modal } from "antd";

import PencilAltIcon from "@/icons/PencilAltIcon";
import TrashIcon from "@/icons/TrashIcon";

import PointOnMapModal from "@/components/modals/PointOnMapModal";

import {
  MAP_STYLES,
  DEFAULT_VIEW_STATE,
  POLYGON_OPACITY,
  POLYGON_STROKE_WIDTH,
  CIRCLE_RADIUS,
} from "@/constants/map";
import { usePolygonPreview } from "../hooks/usePolygonPreview";

const PointField = ({
  field,
  objectUuid,
  value,
  miniature = false,
  width = null,
  height = null,
  showEmpty = false,
  isEdit = false,
  color = "#e66a25",
  onChange,
  onScreenshot = null,
  onComplete,
  onKeyDown,
}) => {
  const mapStyle = {
    width: width ? width : miniature ? "230px" : "450px",
    height: height ? height : miniature ? "160px" : "300px",
  };

  const [viewState, setViewState] = useState(DEFAULT_VIEW_STATE);

  const [showEditModal, setShowEditModal] = useState(false);

  const editButtonRef = useRef(null);

  useEffect(() => {
    if (!value) return;

    const coordinates = Object.values(value);
    if (coordinates.length >= 2) {
      setViewState({
        longitude: coordinates[1],
        latitude: coordinates[0],
        zoom: 15,
      });
    }
  }, [value]);

  useEffect(() => {
    if (!showEditModal) return;

    const timer = setTimeout(() => {
      const firstInput = document.querySelector(
        ".ant-modal-wrap .ant-modal-body input, .ant-modal-wrap .ant-modal-body textarea, .ant-modal-wrap .ant-modal-body [contenteditable='true']",
      );
      firstInput?.focus?.();
    }, 0);

    return () => clearTimeout(timer);
  }, [showEditModal]);

  const point = value ? Object.values(value) : null;
  const hasValidPoint = point && point.length >= 2;

  const pointGeoJSON = useMemo(() => {
    if (!hasValidPoint) return null;

    return {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [point[1], point[0]],
      },
    };
  }, [hasValidPoint, point]);

  const onMapLoad = useCallback(
    (e) => {
      const map = e.target;

      if (hasValidPoint) {
        map.flyTo({
          center: [point[1], point[0]],
          zoom: 15,
          duration: 0,
        });
      }
    },
    [hasValidPoint, point],
  );

  const screenshot = usePolygonPreview(
    pointGeoJSON,
    {
      enabled: miniature || !!onScreenshot,
    },
    color,
    onScreenshot,
  );

  const handleClear = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить точку?`,
      content: "Это действие нельзя будет отменить.",

      onOk() {
        onChange?.(null);
        onComplete?.(null);
      },
      okText: "Удалить",
      cancelText: "Отмена",
      zIndex: 99999,
    });
  };

  return (
    <>
      <Flex
        gap={20}
        wrap
        align="start"
        justify="space-between"
        style={miniature ? { pointerEvents: "none" } : {}}
      >
        {(value || showEmpty) &&
          (miniature ? (
            screenshot ? (
              <img
                src={screenshot}
                style={{
                  ...mapStyle,
                  objectFit: "cover",
                }}
                alt="point preview"
              />
            ) : (
              <div style={mapStyle}>Загрузка...</div>
            )
          ) : (
            <Map
              {...viewState}
              style={mapStyle}
              mapStyle={MAP_STYLES.voyager}
              attributionControl={false}
              interactive
              onMove={(evt) => setViewState(evt.viewState)}
              onLoad={onMapLoad}
            >
              {!miniature && (
                <>
                  <FullscreenControl />
                  <NavigationControl showCompass={false} />
                </>
              )}

              {hasValidPoint && pointGeoJSON && (
                <Source id="point-source" type="geojson" data={pointGeoJSON}>
                  <Layer
                    id="point-circle"
                    type="circle"
                    paint={{
                      "circle-radius": CIRCLE_RADIUS,
                      "circle-color": color,
                      "circle-opacity": POLYGON_OPACITY,
                      "circle-stroke-color": color,
                      "circle-stroke-width": POLYGON_STROKE_WIDTH,
                    }}
                  />
                </Source>
              )}
            </Map>
          ))}
        {!miniature && isEdit && (
          <Flex vertical gap={10}>
            <Button
              data-focus-edit-button="true"
              icon={<PencilAltIcon />}
              onClick={() => setShowEditModal(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowEditModal(true);
                } else {
                  onKeyDown?.(e);
                }
              }}
              ref={editButtonRef}
            >
              Редактировать
            </Button>
            {value && (
              <Button icon={<TrashIcon />} onClick={handleClear}>
                Очистить
              </Button>
            )}
          </Flex>
        )}
      </Flex>

      {showEditModal && (
        <PointOnMapModal
          field={field}
          data={value}
          visible={showEditModal}
          onSave={(val) => {
            setShowEditModal(false);
            onChange?.(val);
            onComplete?.(val);
          }}
          onCancel={() => {
            setShowEditModal(false);
            requestAnimationFrame(() => {
              editButtonRef.current?.focus();
            });
          }}
          color={color}
          objectUuid={objectUuid}
        />
      )}
    </>
  );
};

export default PointField;
